import type { CallGrade } from '../lib/leads/callGrade.js';
import type { CallResultCode } from '../lib/leads/callResult.js';
import type { KyckConnectServices } from '../lib/kyckConnect/services.js';
import type { Grouping } from '../lib/kyckConnect/metrics.js';
import type { IsoTimestamp } from './common.js';

// THE KYCKCONNECT DASHBOARD AND REPORTING WIRE SHAPES (Phase 3). Built field by field on the server from aggregated data.
// ⚠️ NO name, phone number, email, transcript or summary text appears in a REPORT shape: it is counts, medians, percentages and
// bucket keys. The dashboard's "Needs your team" list is the one place a lead is named (the first name of a lead the viewer may see,
// and an opaque lead id to link to); it never carries a number.

export const DASHBOARD_DAYS = [7, 30, 90] as const;
export type DashboardDays = (typeof DASHBOARD_DAYS)[number];

export interface DashboardRelay {
  leads: number;
  delivered: number;
  failed: number;
  inProgress: number;
  /** Delivered / (delivered + failed + in progress), or null with nothing delivered or attempted. */
  deliveredPct: number | null;
  bySource: Array<{ source: string; count: number }>;
  /** One entry per local day in the period, zero-filled (a quiet day is a real zero). */
  perDay: Array<{ date: string; count: number }>;
}

export interface DashboardAiSms {
  /** Conversations where the assistant actually sent a text, over the period. */
  leadsTexted: number;
  replied: number;
  repliedPct: number | null;
  bookedByText: number;
  afterHoursAnswered: number;
  medianFirstTextSeconds: number | null;
}

export interface DashboardSpeedToLead {
  medianSpeedSeconds: number | null;
  /** How many leads the median is over (the calls that dialled the lead). */
  respondedCount: number;
  /** Calls whose chain has finished (the denominator of the reached figure). */
  calls: number;
  reached: number;
  reachedPct: number | null;
  /** Leads that arrived out of hours and were never called: in neither `reached` nor `calls`. */
  outOfHours: number;
  /** Connected calls graded Booked (callGrade). */
  booked: number;
  /** Calls a PERSON started from the lead screen, counted on their own. ⚠️ Never in speed, reached or staff-answer: they answered nothing that arrived. */
  teamCalls: number;
  staffAnswer: { answered: number; of: number; pct: number | null };
  results: Array<{ code: CallResultCode; label: string; count: number }>;
}

export interface DashboardNeedsLead {
  /** The opaque lead id (links into Leads). Never a phone number. */
  leadId: string;
  /** First name only, for the list. Null when the lead has none. */
  name: string | null;
  clubName: string | null;
  receivedAt: IsoTimestamp;
  distress: boolean;
}

export interface DashboardUpcoming {
  appointmentId: string;
  leadName: string;
  clubName: string | null;
  startsAt: IsoTimestamp;
  endsAt: IsoTimestamp;
  timezone: string;
}

export interface KyckConnectDashboard {
  days: DashboardDays;
  from: IsoTimestamp;
  to: IsoTimestamp;
  timezone: string;
  /** The services across the selected clubs (a block shows when ANY selected club has it). */
  services: KyckConnectServices;
  relay: DashboardRelay | null;
  aiSms: DashboardAiSms | null;
  speedToLead: DashboardSpeedToLead | null;
  needs: { count: number; top: DashboardNeedsLead[] };
  upcoming: DashboardUpcoming[];
  /** A read hit its ceiling: the figures are a floor, and the page says so. */
  capped: boolean;
}

// ---- REPORTING ------------------------------------------------------------------------------------------------------------------

export const REPORT_MONTHS = [6, 12] as const;
export type ReportMonths = (typeof REPORT_MONTHS)[number];

export interface SeriesPoint {
  /** The bucket key: `YYYY-MM-DD` (day), the Monday `YYYY-MM-DD` (week) or `YYYY-MM` (month). */
  key: string;
  value: number | null;
}

export interface CallQualityPoint {
  key: string;
  booked: number;
  goodConversation: number;
  notAFit: number;
}

export interface ReportBreakdownRow {
  label: string;
  count: number;
}

export interface StaffReportRow {
  /** The team member's NAME from the club's own profile (a staff member, not a lead). */
  name: string;
  answered: number;
  booked: number;
}

/** day of week (0 = Sunday .. 6) x hour (0 .. 23) -> leads that arrived then, in each lead's own club timezone. */
export interface HeatmapCell {
  dow: number;
  hour: number;
  count: number;
}

export interface ReportPeriod {
  from: IsoTimestamp;
  to: IsoTimestamp;
}

/** Everything one period of the report holds. Series keys are the SAME zero-filled domain for the period. */
export interface ReportPeriodData {
  period: ReportPeriod;
  totals: { leads: number; connectedCalls: number; gradedCalls: number; aiSuggestedGraded: number; teamCalls: number };
  leadsOverTime: SeriesPoint[];
  /** Median seconds from lead to first dial per bucket; null where no call dialled in the bucket (a gap, never a zero). */
  speedTrend: SeriesPoint[];
  callQuality: CallQualityPoint[];
  byStaff: StaffReportRow[];
  bySource: ReportBreakdownRow[];
  heatmap: HeatmapCell[];
  aiSmsReplies: SeriesPoint[];
  aiSmsBookings: SeriesPoint[];
}

export interface KyckConnectReport {
  months: ReportMonths;
  grouping: Grouping;
  timezone: string;
  services: KyckConnectServices;
  current: ReportPeriodData;
  /** The period of the same length immediately before, when comparison was asked for. */
  previous: ReportPeriodData | null;
  /** The leads read hit its ceiling: every figure is a floor, and the page says so. */
  capped: boolean;
}

export type { CallGrade };
